/**
 * Market prices for the Trade Port.
 *
 *  - P2  shipped a static price table read out of the resource catalog.
 *  - P4A makes the market live: supply and demand accumulate into rolling
 *    windows, prices drift on an elasticity curve, and `MarketState` flows
 *    to the client with each snapshot for display.
 *
 * The shared types here describe the *shape* on the wire. The actual price
 * accumulator is server-only (`apps/server/src/sim/marketTracker.ts`) — the
 * client never does simulation, it just renders what the snapshot gives it.
 */
import { z } from 'zod';
import { RESOURCES } from './resources.js';
/**
 * The resource's catalog sticker price. Used as the *baseline* the live
 * price oscillates around — `price = basePrice × multiplier`.
 */
export const BASE_PRICE = Object.freeze(Object.fromEntries(Object.values(RESOURCES).map((r) => [r.id, r.basePrice])));
export function getResourceBaselinePrice(resource) {
    const p = BASE_PRICE[resource];
    if (p === undefined)
        throw new Error(`No baseline price for ${resource}`);
    return p;
}
/** Back-compat shim — P2 callers expected a one-shot `getMarketPrice`. */
export function getMarketPrice(resource) {
    return getResourceBaselinePrice(resource);
}
// ── P4A wire shape ─────────────────────────────────────────────────────────
/**
 * Full market snapshot shipped each world broadcast.
 *
 *   prices       — current live price per resource (clamped)
 *   baseline     — static baseline so the client can render "× of baseline"
 *                   badges without re-importing the catalog
 *   priceHistory — N most recent price samples per resource, oldest→newest,
 *                   sampled at `HISTORY_SAMPLE_EVERY_TICKS` ticks. Used by
 *                   the client sparklines + 24h high/low readouts.
 *   sessionHigh  — session-lifetime max observed price per resource
 *   sessionLow   — session-lifetime min observed price per resource
 */
export const MarketStateSchema = z.object({
    prices: z.record(z.string(), z.number().nonnegative()),
    baseline: z.record(z.string(), z.number().nonnegative()),
    priceHistory: z.record(z.string(), z.array(z.number().nonnegative())),
    sessionHigh: z.record(z.string(), z.number().nonnegative()),
    sessionLow: z.record(z.string(), z.number().nonnegative()),
});
/** How often the MarketTracker samples the current price into history. */
export const HISTORY_SAMPLE_EVERY_TICKS = 10; // 2 s at 5 Hz
/** Max samples retained in `priceHistory`. 60 × 2 s = 2 minutes of chart. */
export const HISTORY_MAX_SAMPLES = 60;
/** Rolling window length for supply/demand accumulation (price computation). */
export const DEFAULT_WINDOW_TICKS = 300; // 1 minute at 5 Hz
/** Default multiplier clamps around baseline (lore §12). */
export const DEFAULT_PRICE_CLAMP_LO = 0.3;
export const DEFAULT_PRICE_CLAMP_HI = 2.0;
/** How aggressively imbalance moves the price. 1.0 = full spec. */
export const DEFAULT_ELASTICITY = 1.0;
/**
 * P28 — Trade Port buy markup. Players can import any resource at the
 * Trade Port for `quantity × marketPrice × BUY_MARKUP`. Round-tripping
 * (buy → sell at the same Trade Port) costs credits, which is exactly
 * the point: the buy command is an escape hatch for "I have no copper
 * deposits on this hub", not free arbitrage. Calibrated at 1.5× —
 * meaningful but not punishing.
 */
export const BUY_MARKUP = 1.5;
/**
 * P28 v2 — Per-faction Trade Port markup adjustment. When a faction
 * event with a `payoutMultiplier > 1` is active for the issuer of the
 * resource being bought, the markup shrinks proportionally — a
 * faction-bonus(helion ×1.4) reduces BUY_MARKUP from 1.5× toward 1.0×
 * for resources Helion has signature on. The math:
 *
 *     adjusted = 1 + (BUY_MARKUP - 1) / payoutMultiplier
 *
 * So payoutMultiplier=1 leaves the markup at 1.5×; payoutMultiplier=2
 * gives 1 + 0.5/2 = 1.25× (a 25% discount on the surcharge); a
 * `trade-embargo` event with payoutMultiplier=0.5 raises it to
 * 1 + 0.5/0.5 = 2× (the surcharge doubles — embargoed factions are
 * harder to buy from too). Floors at 1.0× so the player is never paid
 * to take the resource. The shared helper lives here so the buyResource
 * mutator + the client UI both consume the same formula.
 */
export const BUY_MARKUP_FLOOR = 1.0;
export function adjustedBuyMarkup(payoutMultiplier) {
    if (!payoutMultiplier || payoutMultiplier <= 0)
        return BUY_MARKUP;
    const adjusted = 1 + (BUY_MARKUP - 1) / payoutMultiplier;
    return Math.max(BUY_MARKUP_FLOOR, adjusted);
}
//# sourceMappingURL=market.js.map